import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { List, X } from "@phosphor-icons/react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { scrollToId } from "@/components/Reveal";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

export const Navbar = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "about", label: t.nav.about },
    { id: "why", label: t.nav.why },
    { id: "how", label: t.nav.how },
    { id: "format", label: t.nav.format },
    { id: "gallery", label: t.nav.gallery },
    { id: "investment", label: t.nav.investment },
    { id: "contact", label: t.nav.contact },
  ];

  const go = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: id } });
      setTimeout(() => scrollToId(id), 200);
    } else {
      scrollToId(id);
    }
  };

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[#FAFAF7]/80 backdrop-blur-2xl border-b border-black/5" : "bg-transparent"
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <button
          data-testid="logo"
          onClick={() => go("hero")}
          className="flex items-center gap-2.5"
        >
          <img src="/homezen-mark.png" alt="Home Zen" className="h-9 w-auto" />
          <span className="font-display text-2xl tracking-[0.12em] leading-none">
            HOME<span className="text-[#8B7355]">ZEN</span>
          </span>
        </button>

        <div className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => go(l.id)}
              className="link-underline text-sm tracking-wide text-[#1A1A1A]/80 hover:text-[#1A1A1A] transition-colors"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-5">
          <LanguageSwitcher />
          <button
            data-testid="nav-cta"
            onClick={() => go("contact")}
            className="hidden md:inline-flex bg-[#1A1A1A] text-[#FAFAF7] text-sm px-6 py-3 hover:bg-[#8B7355] transition-colors duration-300"
          >
            {t.nav.cta}
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden text-[#1A1A1A]"
            aria-label="Menu"
          >
            {open ? <X size={26} /> : <List size={26} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-[#FAFAF7]/95 backdrop-blur-2xl border-t border-black/5"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {links.map((l) => (
                <button
                  key={l.id}
                  onClick={() => go(l.id)}
                  className="text-left font-display text-2xl py-2 text-[#1A1A1A]"
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => go("contact")}
                className="mt-4 bg-[#1A1A1A] text-[#FAFAF7] px-6 py-4 text-sm"
              >
                {t.nav.cta}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
